import React, { useEffect, useRef, useState } from 'react'
import { Button, Checkbox, notification, Tooltip, Typography } from 'antd'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Table, { ColumnProps } from 'antd/lib/table'

import { ApplicationState } from 'src/store'
import {
  getUserEmailNotificationsList,
  saveUserEmailNotification
} from 'src/services/api/user-email-notifications'
import { PageHeader } from 'src/components/Ant'
import { UserEmailNotification, Id } from 'src/types'
import useDocumentTitle from 'src/hooks/use-document-title'

import * as CSS from './styles'
import * as Values from './initial-values'
import EmailNotificationsDrawer from './user-email-notification-drawer'
import sanitizeToPost from './sanitize'

type DataSource = UserEmailNotification & { key: Id }

export default function UserEmailNotifications() {
  useDocumentTitle('web.user-email-notification.title')

  const {
    me: {
      user: { organization_id }
    }
  } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [dataSource, setDataSource] = useState<DataSource[]>([])

  const isSubscribed = useRef(true)
  const refFormik = useRef(null)

  const { t } = useTranslation()

  useEffect(
    () => () => {
      isSubscribed.current = false
    },
    []
  )

  useEffect(() => {
    if (!!t && organization_id) {
      setIsLoading(true)
      getUserEmailNotificationsList({ organization_id })
        .then(response => {
          if (isSubscribed.current) {
            if (response.status === 200) {
              setDataSource(
                (response.data?.user_email_notifications || []).map(uEN => ({
                  ...uEN,
                  key: uEN.user_email_notification_id
                })) || []
              )
              return
            }
            notification.warning({
              message: t('common.response.status-500'),
              placement: 'bottomRight'
            })
          }
        })
        .catch(response => {
          if (isSubscribed.current) {
            notification.warning({
              message: t('common.response.status-500'),
              placement: 'bottomRight'
            })
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [t, organization_id])

  const title = 'web.user-email-notification.title'
  const columns: ColumnProps<UserEmailNotification>[] = [
    {
      dataIndex: 'notification_status',
      render: (notification_status: string) => {
        if (!notification_status) {
          return <></>
        }
        return (
          <Typography.Text>
            {t(`common.status.unified.${notification_status}`.toLowerCase())}
          </Typography.Text>
        )
      },
      title: (
        <Typography.Text strong>
          {t('web.user-email-notification.situation')}
        </Typography.Text>
      )
    },
    {
      dataIndex: 'email',
      render: (email: string = '') => {
        if (email?.length > 50) {
          return (
            <Tooltip title={email}>
              <Typography.Text>
                {`${email.substring(0, 47)}...`}
              </Typography.Text>
            </Tooltip>
          )
        }
        return <Typography.Text>{email}</Typography.Text>
      },
      title: (
        <Typography.Text strong>
          {t('web.user.common.email.title')}
        </Typography.Text>
      )
    },
    {
      align: 'center',
      dataIndex: 'validate_reason',
      render: (validate_reason: boolean) => (
        <Checkbox disabled checked={validate_reason} />
      ),
      title: (
        <Typography.Text strong>
          {t('web.user-email-notification.validate-reason')}
        </Typography.Text>
      )
    },
    {
      dataIndex: 'reason',
      render: ({ name }: { name: string }) => (
        <Typography.Text>{name}</Typography.Text>
      ),
      title: (
        <Typography.Text strong>
          {t('web.resources.reasons.title')}
        </Typography.Text>
      )
    },
    {
      align: 'center',
      dataIndex: 'attach_travel_report',
      render: (attach_travel_report: boolean) => (
        <Checkbox disabled checked={attach_travel_report} />
      ),
      title: (
        <Typography.Text strong>
          {t('web.user-email-notification.attach-travel-report')}
        </Typography.Text>
      )
    },
    {
      align: 'center',
      dataIndex: '',
      render: record => (
        <CSS.Switch
          checked={record.active}
          data-test-id="email-notification-active"
          onChange={() => {
            const user_email_notifications = {
              ...record,
              active: !record.active
            }
            setIsLoading(true)
            saveUserEmailNotification({
              organization_id,
              user_email_notifications
            })
              .then(response => {
                if (isSubscribed.current) {
                  if (response.status === 200) {
                    const updateRecord =
                      response.data?.user_email_notifications?.[0]

                    setDataSource(list =>
                      list.map(data =>
                        data.user_email_notification_id ===
                        updateRecord?.user_email_notification_id
                          ? {
                              ...updateRecord,
                              key: updateRecord.user_email_notification_id
                            }
                          : data
                      )
                    )
                    return
                  }
                  notification.warning({
                    message: t('common.response.status-500'),
                    placement: 'bottomRight'
                  })
                }
              })
              .catch(response => {
                if (isSubscribed.current) {
                  notification.warning({
                    message: t('common.response.status-500'),
                    placement: 'bottomRight'
                  })
                }
              })
              .finally(() => {
                setIsLoading(false)
              })
          }}
        />
      ),
      title: <Typography.Text strong>{t('common.active')}</Typography.Text>
    },
    {
      align: 'center',
      dataIndex: '',
      render: record => (
        <Button
          data-test-id="email-notification-edit"
          icon="edit"
          onClick={() => {
            refFormik.current?.resetForm(record)
          }}
          type="ghost"
        />
      ),
      title: (
        <Typography.Text strong>{t('common.actions.edit')}</Typography.Text>
      )
    }
  ]

  function handleSubmit(values: DataSource): void {
    setIsLoading(true)
    const sanitized = sanitizeToPost(values)
    saveUserEmailNotification({
      organization_id,
      user_email_notifications: [sanitized]
    })
      .then(response => {
        if (isSubscribed.current) {
          if (response.status === 200) {
            const updateRecord = response.data?.user_email_notifications?.[0]

            setDataSource(list =>
              list.map(data =>
                data.user_email_notification_id ===
                updateRecord?.user_email_notification_id
                  ? {
                      ...updateRecord,
                      key: updateRecord.user_email_notification_id
                    }
                  : data
              )
            )
            refFormik.current?.resetForm(Values.initialValues)
            return
          }
          notification.warning({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        }
      })
      .catch(response => {
        if (isSubscribed.current) {
          notification.warning({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return (
    <>
      <PageHeader
        extra={[
          <Button
            data-test-id="email-notification-add"
            key="common.actions.add"
            onClick={() => {
              refFormik.current?.resetForm(
                Values.getDefaultValue(organization_id)
              )
            }}
            size="default"
            type="primary"
          >
            {t('common.actions.add')}
          </Button>
        ]}
        title={title}
      />
      <CSS.Content>
        <Table
          className="table-striped"
          columns={columns}
          data-test-id="email-notification-list"
          dataSource={dataSource}
          loading={isLoading}
          locale={{
            emptyText: t('common.no-data')
          }}
          pagination={false}
        />
      </CSS.Content>
      <EmailNotificationsDrawer
        handleSubmit={handleSubmit}
        isLoading={isLoading}
        refFormik={refFormik}
      />
    </>
  )
}
